import React, { createContext, useContext, useState, useEffect, useMemo, ReactNode } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from './AuthContext';
import { useCurrentProfile } from '@/hooks/useCurrentProfile';

// Base user roles (independent from seller status)
export type UserRole = 
  | 'admin' 
  | 'visitor' 
  | 'student' 
  | 'partner' 
  | 'staff';

// Seller status type info
export interface SellerTypeInfo {
  id: string;
  name: string;
  label: string;
  max_listings_per_day: number;
  expiration_days: number;
  priority_bonus: number;
}

export const ROLE_LABELS: Record<UserRole, string> = {
  admin: 'Admin',
  visitor: 'Visitante',
  student: 'Estudante',
  partner: 'Parceiro',
  staff: 'Staff',
};

export const ALL_ROLES: UserRole[] = [
  'admin',
  'visitor',
  'student',
  'partner',
  'staff',
];

interface RoleContextType {
  currentRole: UserRole;
  simulatedRole: UserRole | null;
  effectiveRole: UserRole;
  setCurrentRole: (role: UserRole) => void;
  setSimulatedRole: (role: UserRole | null) => void;
  sellerType: SellerTypeInfo | null;
  isAdmin: boolean;
  isSimulating: boolean;
  isSeller: boolean;
  canActAsSeller: boolean;
  isVerifiedSeller: boolean;
  isSellerSuspended: boolean;
  sellerSuspendedAt: string | null;
  loadingRole: boolean;
  roleFetched: boolean;        // True when role data is real (from cache or DB)
  refreshRole: () => void;
  clearRoleCache: () => void;
}

const RoleContext = createContext<RoleContextType | undefined>(undefined);

// Cache key and helpers for instant hydration
const ROLE_CACHE_KEY = 'forja:role:v2'; // Updated version for new fields

interface CachedRoleData {
  role: UserRole;
  sellerType: SellerTypeInfo | null;
  isVerifiedSeller: boolean;
  sellerSuspendedAt: string | null;
  timestamp: number;
}

// Stale-while-revalidate strategy:
// - Cache is HARD-EXPIRED after 24h (only then we block UI on a fresh fetch).
// - Cache is considered STALE after 5min — UI keeps rendering with cached
//   role while a silent background refetch updates it.
// This eliminates the "Preparando sua forja..." loader when returning to the
// tab after medium inactivity (5min – 1h).
const ROLE_CACHE_HARD_TTL_MS = 24 * 60 * 60 * 1000; // 24h
const ROLE_CACHE_STALE_AFTER_MS = 5 * 60 * 1000;    // 5min

const getCachedRoleData = (userId: string): CachedRoleData | null => {
  try {
    const cached = localStorage.getItem(`${ROLE_CACHE_KEY}:${userId}`);
    if (!cached) return null;
    const data = JSON.parse(cached) as CachedRoleData;
    // Hard expiration only — stale entries are still returned and revalidated
    // in background by the effect below.
    if (Date.now() - data.timestamp > ROLE_CACHE_HARD_TTL_MS) {
      localStorage.removeItem(`${ROLE_CACHE_KEY}:${userId}`);
      return null;
    }
    return data;
  } catch {
    return null;
  }
};

/** Returns true when cache exists but is older than the stale threshold. */
export const isRoleCacheStale = (data: CachedRoleData | null): boolean => {
  if (!data) return false;
  return Date.now() - data.timestamp > ROLE_CACHE_STALE_AFTER_MS;
};

/** Lightweight read used by AppReadyGate to know if we have any usable cache. */
export const hasAnyCachedRole = (userId: string | undefined): boolean => {
  if (!userId) return false;
  return !!getCachedRoleData(userId);
};

const setCachedRoleData = (userId: string, data: Omit<CachedRoleData, 'timestamp'>) => {
  try {
    localStorage.setItem(`${ROLE_CACHE_KEY}:${userId}`, JSON.stringify({
      ...data,
      timestamp: Date.now()
    }));
  } catch {
    // Ignore storage errors
  }
};

export function RoleProvider({ children }: { children: ReactNode }) {
  const { user } = useAuth();
  
  // Try to hydrate from cache immediately for instant display
  const cachedData = user ? getCachedRoleData(user.id) : null;
  
  // Consume profile from the unified cache
  const { data: profileData, isLoading: profileLoading } = useCurrentProfile(user?.id);
  
  // Default role is 'visitor' for new/unauthenticated users
  const [currentRole, setCurrentRole] = useState<UserRole>(cachedData?.role ?? 'visitor');
  const [simulatedRole, setSimulatedRole] = useState<UserRole | null>(null);
  
  const [loadingRole, setLoadingRole] = useState(() => {
    if (!user) return false;
    return !cachedData;
  });
  
  // Seller type info (dynamic from database)
  const [sellerType, setSellerType] = useState<SellerTypeInfo | null>(cachedData?.sellerType ?? null);
  
  // Derive from profile cache (with localStorage fallback for instant hydration)
  const isVerifiedSeller = profileData
    ? Boolean(
        (currentRole === 'admin' || currentRole === 'staff') ||
        (profileData.is_verified && (!profileData.verification_expires_at || new Date(profileData.verification_expires_at) > new Date()))
      )
    : (cachedData?.isVerifiedSeller ?? false);
  
  const sellerSuspendedAt = profileData?.seller_suspended_at ?? cachedData?.sellerSuspendedAt ?? null;
  
  // Whether role data has been resolved (from cache or fetch)
  const [roleFetched, setRoleFetched] = useState<boolean>(!!cachedData);
  
  // Refresh trigger
  const [refreshTrigger, setRefreshTrigger] = useState(0);
  const refreshRole = () => setRefreshTrigger(prev => prev + 1);
  
  // Clear cache and force refresh (useful after verification approval)
  const clearRoleCache = () => {
    if (user) {
      localStorage.removeItem(`${ROLE_CACHE_KEY}:${user.id}`);
      refreshRole();
    }
  };

  // Fetch user role from database when user changes
  useEffect(() => {
    async function fetchUserRoleAndStatus() {
      if (!user) {
        setCurrentRole('visitor');
        setSellerType(null);
        setLoadingRole(false);
        setRoleFetched(false);
        return;
      }

      // Re-hydrate from cache when `user` arrives late (e.g. cold boot
      // where AuthContext resolved getSession() after RoleProvider mounted).
      // Without this, currentRole stays 'visitor' until the network fetch
      // completes, which can flash admin UI as visitor for several seconds.
      const lateCache = getCachedRoleData(user.id);
      if (lateCache && !roleFetched) {
        setCurrentRole(lateCache.role);
        setSellerType(lateCache.sellerType);
        setRoleFetched(true);
        setLoadingRole(false);
      }

      try {
        // Cérebro is authoritative for role and commercial access.
        const { data: accessData, error: rolesError } = await (supabase as any)
          .rpc('current_forjahub_access');

        if (rolesError) {
          console.error('Error fetching roles:', rolesError);
        }

        const access = Array.isArray(accessData) ? accessData[0] : accessData;
        const newRole: UserRole = access?.can_access && ALL_ROLES.includes(access.role)
          ? access.role
          : 'visitor';
        
        setCurrentRole(newRole);

        // Fetch seller type info if user has one (from profile cache)
        let newSellerType: SellerTypeInfo | null = null;
        // Seller plans belong to the Marketplace cutover and are not inferred
        // from CRM commercial fields.
        
        setSellerType(newSellerType);

        // Cache the data for instant hydration on next visit
        setCachedRoleData(user.id, {
          role: newRole,
          sellerType: newSellerType,
          isVerifiedSeller: Boolean(
            newRole === 'admin' || newRole === 'staff' || (profileData?.is_verified &&
            (!profileData?.verification_expires_at || 
             new Date(profileData.verification_expires_at) > new Date()))
          ),
          sellerSuspendedAt: profileData?.seller_suspended_at || null,
        });

      } catch (error) {
        console.error('Error fetching user data:', error);
        setCurrentRole('visitor');
        setSellerType(null);
      } finally {
        setLoadingRole(false);
        setRoleFetched(true);
      }
    }

    fetchUserRoleAndStatus();
  }, [user, refreshTrigger, profileData]);
  
  const effectiveRole = simulatedRole ?? currentRole;
  
  const isAdmin = currentRole === 'admin';
  const isSimulating = simulatedRole !== null;
  
  // REAL seller permission (for database actions - ignores simulation)
  // A user can act as seller ONLY if they have:
  // 1. An active seller_type_id (paid plan) OR is a verified seller
  // 2. AND must have student/admin/staff role (visitors cannot sell)
  const hasSellerPlan = isVerifiedSeller || sellerType !== null;
  const hasRequiredRole = currentRole === 'student' || currentRole === 'admin' || currentRole === 'staff';
  const canActAsSeller = hasSellerPlan && hasRequiredRole;
  
  // Seller suspended: has suspension timestamp AND is not currently student/admin/staff
  const isSellerSuspended = Boolean(
    sellerSuspendedAt && 
    currentRole !== 'student' && 
    currentRole !== 'admin' && 
    currentRole !== 'staff'
  );
  
  // UI visibility permission (respects simulation)
  // When simulating, only show seller UI if effectiveRole is admin or staff
  const isSeller = isSimulating
    ? (effectiveRole === 'admin' || effectiveRole === 'staff')
    : canActAsSeller;

  const contextValue = useMemo(() => ({
    currentRole,
    simulatedRole,
    effectiveRole,
    setCurrentRole,
    setSimulatedRole,
    sellerType,
    isAdmin,
    isSimulating,
    isSeller,
    canActAsSeller,
    isVerifiedSeller,
    isSellerSuspended,
    sellerSuspendedAt,
    loadingRole,
    roleFetched,
    refreshRole,
    clearRoleCache,
  }), [
    currentRole, simulatedRole, effectiveRole,
    sellerType, isAdmin, isSimulating,
    isSeller, canActAsSeller, isVerifiedSeller,
    isSellerSuspended, sellerSuspendedAt,
    loadingRole, roleFetched,
    refreshRole, clearRoleCache,
  ]);

  return (
    <RoleContext.Provider value={contextValue}>
      {children}
    </RoleContext.Provider>
  );
}

export function useRole() {
  const context = useContext(RoleContext);
  if (context === undefined) {
    throw new Error('useRole must be used within a RoleProvider');
  }
  return context;
}
